import React, { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { ArrowRight, CheckCircle } from '@phosphor-icons/react';

const MIN_LEN = 8;

export default function ResetPassword() {
  const { user, checked, resetPassword } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (checked && user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (password.length < MIN_LEN) { setErr(t('reset_too_short')); return; }
    if (password !== confirm) { setErr(t('reset_mismatch')); return; }
    setLoading(true);
    const res = await resetPassword(token, password);
    setLoading(false);
    if (res.ok) {
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } else {
      setErr(res.error || t('reset_failed'));
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-bg p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <img src="/favicon.png" alt={t('brand')} className="w-9 h-9 rounded-lg bg-sage-100 p-1 object-contain" />
          <div className="font-heading text-xl text-ink">{t('brand')}</div>
        </div>

        <h2 className="font-heading text-3xl text-ink mb-2">{t('reset_title')}</h2>
        <p className="text-sm text-ink-soft mb-8">{t('reset_sub')}</p>

        {!token ? (
          <div data-testid="reset-no-token" className="text-sm text-terracotta-700 bg-terracotta-100 rounded-xl px-4 py-4">
            {t('reset_no_token')}
          </div>
        ) : done ? (
          <div data-testid="reset-success" className="text-sm text-sage-700 bg-sage-100 rounded-xl px-4 py-4 flex items-center gap-2">
            <CheckCircle size={20} weight="fill" className="text-sage-600 shrink-0" />
            {t('reset_success')}
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-form" className="space-y-4 animate-fade-up">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-ink-muted font-semibold">
                {t('reset_new_password')}
              </label>
              <input
                type="password" required minLength={MIN_LEN}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="reset-password-input"
                className="mt-2 w-full px-4 py-3 rounded-xl border border-stone_line bg-white focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage-100 transition-all"
                placeholder="••••••••"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-ink-muted font-semibold">
                {t('reset_confirm_password')}
              </label>
              <input
                type="password" required minLength={MIN_LEN}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm-input"
                className="mt-2 w-full px-4 py-3 rounded-xl border border-stone_line bg-white focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage-100 transition-all"
                placeholder="••••••••"
              />
            </div>
            <p className="text-xs text-ink-muted">{t('reset_hint')}</p>

            {err && (
              <div data-testid="reset-error" className="text-sm text-terracotta-700 bg-terracotta-100 rounded-lg px-4 py-3">
                {err}
              </div>
            )}

            <button
              type="submit" disabled={loading}
              data-testid="reset-submit-button"
              className="w-full mt-2 bg-sage hover:bg-sage-600 text-white font-medium px-6 py-3.5 rounded-xl transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? t('loading') : t('reset_submit')}
              {!loading && <ArrowRight size={18} weight="bold" />}
            </button>
          </form>
        )}

        <div className="mt-8 text-sm text-ink-soft">
          <button
            type="button"
            onClick={() => navigate('/login')}
            data-testid="reset-back-to-login"
            className="text-sage font-medium hover:text-sage-600"
          >
            {t('forgot_back')}
          </button>
        </div>
      </div>
    </div>
  );
}
